import { Card, SkeletonBox } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { InvoiceStatus, useInvoicePaymentDetailQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { PayInvoiceHeader } from '../PayInvoiceHeader/PayInvoiceHeader'

import type { InvoiceDetail } from '@shared/graphql'

export interface InvoiceSummaryCardProps {
  /**
   * The invoice detail that should be displayed.
   */
  invoiceDetail: InvoiceDetail
  /**
   * Indicate whether a loading state should be shown.
   */
  loading?: boolean
  /**
   * Additional classes that will be applied to the element.
   */
  className?: string
}

export const InvoiceSummary: React.FC<InvoiceSummaryCardProps> = ({
  className,
  invoiceDetail,
  loading = false,
  ...props
}) => {
  const { t } = useTranslation('settings')
  const { money } = useMoney()
  const invoicePaymentDetail = useInvoicePaymentDetailQuery({
    variables: {
      id: invoiceDetail.id,
    },
  })

  return (
    <Card className={className} {...props}>
      <Card.Content className="px-10 py-8 font-circular">
        <div
          className="flex h-18 items-center"
          data-testid="invoice-summary-block"
        >
          <div>
            <SkeletonBox className="h-9 w-64" loading={loading}>
              <div className="h-9 text-3xl leading-9 text-black-600 font-circular-medium">
                <span>
                  <SkeletonBox loading={loading}>
                    {money({
                      number: invoiceDetail.amountDue.amount,
                      variant: invoiceDetail.amountDue.currency,
                      options: {
                        symbol: false,
                        signDisplay: false,
                      },
                    })}
                  </SkeletonBox>
                </span>
                <span className="ml-2 text-xl leading-7 text-black-200 font-circular-regular">
                  {invoiceDetail?.amountDue.currency}
                </span>
              </div>
            </SkeletonBox>
            <div className="mt-1 text-xl leading-7 text-black-200 font-circular-regular">
              {t('billing.invoices.details.summary.amountDue')}
            </div>
          </div>
          <SkeletonBox loading={loading || invoicePaymentDetail.loading}>
            <PayInvoiceHeader
              invoiceId={invoiceDetail?.id}
              invoiceStatus={
                invoicePaymentDetail.data?.invoicePaymentDetail
                  .latestPaymentDetails?.status === InvoiceStatus.pending
                  ? InvoiceStatus.pending
                  : invoiceDetail?.status
              }
            />
          </SkeletonBox>
        </div>
      </Card.Content>
    </Card>
  )
}
